import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Printer, ArrowLeft, Loader2 } from 'lucide-react';
import { getCotizacionById, crearCotizacion, actualizarCotizacion } from '../api';
import './Cotizaciones.css';

const Cotizaciones = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = id && id !== 'nueva';

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    num: '',
    empresa: '',
    at: '',
    saludo: '',
    precio: '1,8 sueldos brutos más IVA por cargo',
    notap: 'Si el candidato es referido por el cliente: 1,2 sueldos brutos más IVA',
    intro: 'Junto con saludarte y según lo conversado, te adjunto cotización por el servicio de búsqueda y selección de los cargos indicados a continuación.'
  });

  const [cargos, setCargos] = useState([{ nombre: '', cantidad: '1' }]);
  const [notas, setNotas] = useState([
    'La consultora garantiza la presentación de una terna (3 candidatos viables) en un plazo de 15 a 20 días hábiles.',
    'Garantía de reposición sin costo si el candidato seleccionado renuncia o es desvinculado por desempeño en los primeros 3 meses.',
    'Los valores indicados no incluyen el IVA, el cual será agregado al momento de la facturación.'
  ]);

  useEffect(() => {
    if (isEditing) {
      const loadCotizacion = async () => {
        try {
          const res = await getCotizacionById(id);
          const data = res.data;
          setFormData({
            num: data.num || '',
            empresa: data.empresa || '',
            at: data.at || '',
            saludo: data.saludo || '',
            precio: data.precio || '',
            notap: data.notap || '',
            intro: data.intro || ''
          });
          setCargos(data.cargos || []);
          setNotas(data.notas || []);
        } catch (err) {
          console.error("Error cargando cotización:", err);
          alert("Error al cargar los datos de la cotización.");
        } finally {
          setLoading(false);
        }
      };
      loadCotizacion();
    }
  }, [id, isEditing]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData({ ...formData, [id.replace('f-', '')]: value });
  };

  const addCargo = () => setCargos([...cargos, { nombre: '', cantidad: '1' }]);
  const updateCargo = (idx, field, val) => {
    const newCargos = [...cargos];
    newCargos[idx][field] = val;
    setCargos(newCargos);
  };
  const removeCargo = (idx) => setCargos(cargos.filter((_, i) => i !== idx));

  const addNota = () => setNotas([...notas, '']);
  const updateNota = (idx, val) => {
    const newNotas = [...notas];
    newNotas[idx] = val;
    setNotas(newNotas);
  };
  const removeNota = (idx) => setNotas(notas.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setSaving(true);
    const data = { ...formData, cargos, notas };
    try {
      if (isEditing) {
        await actualizarCotizacion(id, data);
      } else {
        await crearCotizacion(data);
      }
      navigate('/cotizaciones');
    } catch (err) {
      console.error("Error guardando cotización:", err);
      alert("Error al guardar la cotización.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20 bg-[#C2BDB4]">
        <Loader2 size={48} className="text-white animate-spin mb-4" />
        <p className="text-white font-medium">Cargando editor...</p>
      </div>
    );
  }

  return (
    <div className="cotizaciones-wrapper">
      <div className="editor">
        <div className="flex justify-between items-center mb-6">
          <button 
            onClick={() => navigate('/cotizaciones')}
            className="text-[#2C4F1A] flex items-center gap-2 hover:underline font-medium text-sm"
          >
            <ArrowLeft size={16} /> Volver al historial
          </button>
          <div className="flex gap-3">
             <button 
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 hover:bg-gray-200 transition"
                onClick={() => window.print()}
              >
                <Printer size={16} /> VISTA PREVIA / IMPRIMIR
              </button>
              <button 
                className="bg-[#2C4F1A] text-white px-6 py-2 rounded-lg text-sm font-bold flex items-center gap-2 hover:bg-opacity-90 transition disabled:opacity-50"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {isEditing ? 'ACTUALIZAR GUARDADO' : 'GUARDAR COTIZACIÓN'}
              </button>
          </div>
        </div>

        <h3>Editor de Cotización</h3>
        <div className="eg">
          <div className="ef"><label>Empresa cliente</label><input id="f-empresa" value={formData.empresa} onChange={handleInputChange} /></div>
          <div className="ef"><label>At: (contactos)</label><input id="f-at" value={formData.at} onChange={handleInputChange} /></div>
          <div className="ef"><label>Saludo (nombre)</label><input id="f-saludo" value={formData.saludo} onChange={handleInputChange} /></div>
          <div className="ef"><label>N° de referencia</label><input id="f-num" value={formData.num} onChange={handleInputChange} /></div>
          <div className="ef"><label>Valor del servicio</label><input id="f-precio" value={formData.precio} onChange={handleInputChange} /></div>
          <div className="ef"><label>Nota de precio</label><input id="f-notap" value={formData.notap} onChange={handleInputChange} /></div>

          <div className="cw mt-4">
            <label>Cargo(s) — Nombre y cantidad</label>
            <div>
              {cargos.map((cargo, index) => (
                <div className="cr" key={index}>
                  <input placeholder="Nombre del cargo" value={cargo.nombre} onChange={(e) => updateCargo(index, 'nombre', e.target.value)} />
                  <input type="number" placeholder="Cant." value={cargo.cantidad} onChange={(e) => updateCargo(index, 'cantidad', e.target.value)} />
                  <button className="brm" onClick={() => removeCargo(index)}>×</button>
                </div>
              ))}
            </div>
            <button className="badd" onClick={addCargo}>+ Agregar cargo</button>
          </div>

          <div className="ef full mt-4">
            <label>Texto introductorio</label>
            <textarea id="f-intro" value={formData.intro} onChange={handleInputChange} />
          </div>

          <div className="cw mt-4">
            <label>Notas comerciales</label>
            <table className="ned">
              <tbody>
                {notas.map((nota, index) => (
                  <tr key={index}>
                    <td className="ni">{index + 1}.</td>
                    <td><textarea value={nota} onChange={(e) => updateNota(index, e.target.value)} /></td>
                    <td><button className="brm" onClick={() => removeNota(index)}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className="badd" onClick={addNota}>+ Agregar nota</button>
          </div>
        </div>
      </div>

      <div className="doc">
        <div className="head-blanco">
          <div className="text-2xl font-bold tracking-widest text-[#2C4F1A] uppercase flex items-center gap-2" style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>
            <div className="flex items-center justify-center p-1 bg-[#2C4F1A] rounded text-white">
              <span className="text-xl">HP</span>
            </div>
            HUNTINGPLUS<span className="text-sm font-normal text-[#9A9A9A] ml-2 tracking-normal">Consultores</span>
          </div>
          <div className="head-right">
            <div className="head-empresa-label">PROPUESTA COMERCIAL</div>
            <div className="head-subtitulo">Búsqueda y Selección de Talento</div>
          </div>
        </div>

        <div className="head-verde">
          <div className="head-tit">Cotización de Servicios</div>
          <div className="head-meta">
            <div>
              <div className="hm-lbl">N° Ref</div>
              <div className="hm-val">{formData.num || '---'}</div>
            </div>
            <div>
              <div className="hm-lbl">Fecha</div>
              <div className="hm-val">{new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
            </div>
          </div>
        </div>
        
        <div className="head-oro"></div>

        <div className="doc-body">
          <div className="dest">
            <div className="dest-lbl">Presentado a</div>
            <div className="dest-empresa">{formData.empresa || '[Empresa Cliente]'}</div>
            <div className="dest-at">Atención: {formData.at || '[Contactos]'}</div>
          </div>

          <div className="intro">
            Estimado/a {formData.saludo || '[Nombre]'},<br/><br/>
            {formData.intro}
          </div>

          <div className="stit">CARGOS SOLICITADOS</div>
          <div className="svcs">
            {cargos.length > 0 ? cargos.map((cargo, index) => (
              <div className="svc" key={index}>
                <div className="svc-n">{cargo.cantidad || '-'}</div>
                <div className="svc-t"><strong>{cargo.nombre || 'Nombre del Cargo'}</strong> - Servicio integral de reclutamiento enfocado en la detección de talento calificado.</div>
              </div>
            )) : <p className="text-sm italic text-gray-400">Sin cargos definidos.</p>}
          </div>

          <div className="stit mt-6">INVERSIÓN DEL SERVICIO</div>
          <div className="precio-wrap">
            <div className="precio-main">
              <div className="precio-lbl">Tarifa Profesional</div>
              <div className="precio-val">{formData.precio || '---'}</div>
              <div className="precio-sub">{formData.notap}</div>
            </div>
            <div className="precio-pagos">
               <div className="ppago">
                  <div className="ppago-lbl">Inicio del proceso</div>
                  <div className="ppago-val">30% del honorario</div>
               </div>
               <div className="ppago">
                  <div className="ppago-lbl">Al cierre del proceso</div>
                  <div className="ppago-val">70% restante</div>
               </div>
            </div>
          </div>

          <div className="stit mt-6">CONDICIONES COMERCIALES</div>
          <div className="notas-blq">
            <ol>
              {notas.map((nota, index) => (
                <li key={index}><strong>0{index + 1}.</strong> {nota}</li>
              ))}
            </ol>
          </div>

          <div className="cierre mt-6">
            Quedamos a su disposición para cualquier consulta adicional sobre esta oferta comercial.
          </div>

          <div className="firma mt-8">
            <div>
              <div className="fi-nom">Equipo HuntingPlus</div>
              <div className="fi-cargo">Consultoría Estratégica</div>
            </div>
          </div>

          <div className="acp" style={{ marginTop: '3rem' }}>
            <strong>ACEPTACIÓN DE PROPUESTA COMERCIAL</strong>
            Nombre y Firma Representante ______________________________________ Fecha: ___/___/20__
          </div>
        </div>

        <div className="doc-foot">
          <div className="text-white font-bold opacity-30 tracking-widest text-xs">HUNTINGPLUS</div>
          <div className="ft-i">
            HuntingPlus Consultores • Santiago, Chile<br/>
            <span className="opacity-60">Software de Gestión de Procesos v2.0</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cotizaciones;
